import { Holding, MacroIndicators, MarketSignal, WatchlistItem, PolicyRatesData, QuantRecommendation } from '../types';
import { sanitizeMacroState } from '../utils/macro_utils';
import { auth } from '../firebase';
import { getDeviceInfoPayload } from '../utils/deviceInfo';
import { StorageManager } from '../utils/storageManager';

/**
 * Ensures the response is valid JSON before attempting to parse.
 * Prevents "Unexpected token '<'" error when Vite or Server returns HTML (fallback).
 */
async function safeJson(res: Response, fallback: any = null) {
  if (!res.ok) return fallback;
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch (e) {
      console.warn('[API] JSON parse error despite header:', e);
      return fallback;
    }
  }
  console.warn(`[API] Expected JSON but received ${contentType || 'unknown'}. Skipping parse.`);
  return fallback;
}
// Fully removed direct Firestore imports as database is unified to Postgres locally

// Helper for fetch with timeout
async function fetchWithTimeout(resource: string, options: any = {}) {
  const { timeout = 10000 } = options;

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const headers = new Headers(options.headers || {});
  if (!headers.has('X-App-Version')) {
    headers.set('X-App-Version', typeof __APP_PRODUCT_VERSION__ !== 'undefined' ? __APP_PRODUCT_VERSION__ : '1.0');
  }
  if (!headers.has('X-Build-Version')) {
    headers.set('X-Build-Version', typeof __APP_BUILD_VERSION__ !== 'undefined' ? __APP_BUILD_VERSION__ : '1.134.3');
  }

  try {
    const response = await fetch(resource, {
      ...options,
      headers,
      credentials: 'include',
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error: any) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      const timeoutError = new Error(`Request timed out after ${timeout}ms`);
      timeoutError.name = 'TimeoutError';
      throw timeoutError;
    }
    throw error;
  }
}

/**
 * Wraps fetchWithTimeout with bounded retries and exponential backoff.
 * Used for heavy endpoints (signals / bulk prices) that may transiently time out.
 */
async function fetchWithRetry(resource: string, options: any = {}, maxRetries?: number): Promise<Response> {
  const effectiveRetries = options.retries !== undefined ? options.retries : (maxRetries !== undefined ? maxRetries : 2);
  const { timeout = 10000, retries, ...rest } = options;
  let lastError: any = null;
  for (let attempt = 0; attempt <= effectiveRetries; attempt++) {
    try {
      const res = await fetchWithTimeout(resource, { ...rest, timeout });
      if (res.status >= 500 && attempt < effectiveRetries) {
        const backoffMs = Math.min(500 * Math.pow(2, attempt), 2000);
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        continue;
      }
      return res;
    } catch (e) {
      lastError = e;
      if (attempt < effectiveRetries) {
        const backoffMs = Math.min(500 * Math.pow(2, attempt), 2000);
        await new Promise(resolve => setTimeout(resolve, backoffMs));
      }
    }
  }
  throw lastError;
}

// Safe getIdToken with timeout
async function getSafeToken(user: any): Promise<string | null> {
  if (!user) return null;
  try {
    return await Promise.race([
      user.getIdToken(),
      new Promise<null>((_, reject) => setTimeout(() => reject(new Error('Token timeout')), 5000))
    ]) as string;
  } catch (e) {
    console.error("Token fetch failed", e);
    return null;
  }
}

async function getAuthHeaders(user: any) {
  const deviceInfo = getDeviceInfoPayload();
  const baseHeaders: Record<string, string> = {
    'X-Device-Id': deviceInfo.deviceId,
    'X-Device-Name': deviceInfo.deviceName,
  };
  if (!user) return baseHeaders;
  const token = await getSafeToken(user);
  return token ? { ...baseHeaders, 'Authorization': `Bearer ${token}` } : baseHeaders;
}

export async function fetchMacroIndicators(market: 'US' | 'KR' = 'US', forceRefresh: boolean = false): Promise<MacroIndicators> {
  const userId = auth.currentUser?.uid;

  // Scoped keys for multi-tenancy
  const CACHE_KEY = userId
    ? `mmo_${userId}_indicators_cache_v2_${market}`
    : `macroOptix_indicators_cache_v2_${market}`;

  const STORAGE_KEY = userId
    ? `mmo_${userId}_apiKeys`
    : 'macroOptix_apiKeys';

  const CACHE_TTL = 30 * 60 * 1000;

  // Always remove stale cache that lacks government field
  if (!forceRefresh) {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        // Validate that data has fedFunds, government, AND economics objects
        const hasGovernment = data?.government && typeof data.government === 'object' && (
          data.government.tgaBalance || data.government.fedBalanceSheet ||
          data.government.reverseRepo || data.government.fedReserves ||
          data.government.highYieldSpread || data.government.treasury3M
        );
        const hasEconomics = data?.economics && typeof data.economics === 'object' && (
          data.economics.coreCpi || data.economics.corePce || data.economics.pmiManufacturing
        );
        if (Date.now() - timestamp < CACHE_TTL && data && typeof data === 'object' && data.fedFunds && hasGovernment && hasEconomics) {
          return data;
        }
        // Stale or incomplete cache — remove it so fresh data is fetched
        localStorage.removeItem(CACHE_KEY);
      } catch (e) {
        localStorage.removeItem(CACHE_KEY);
      }
    }
  } else {
    localStorage.removeItem(CACHE_KEY);
  }

  const saved = localStorage.getItem(STORAGE_KEY);
  let fredKey = '';
  if (saved) {
    try {
      const keys = JSON.parse(saved);
      fredKey = keys.fred || '';
    } catch (e) { }
  }

  try {
    const res = await fetchWithTimeout(`/api/macro/indicators?market=${market}`, {
      headers: {
        'x-fred-api-key': fredKey,
        ...(await getAuthHeaders(auth.currentUser))
      },
      timeout: 60000 // Sufficient time for heavy external API aggregations (60s)
    });
    if (!res.ok) throw new Error(`Failed to fetch macro indicators, status: ${res.status}`);

    const data = await res.json() as any;
    const today = new Date().toISOString().split('T')[0];
    if (!data.government || typeof data.government !== 'object') {
      data.government = {};
    }
    // Ensure all keys exist without overwriting valid non-zero data
    const govKeys: Record<string, { value: number; date: string; change: number }> = {
      tgaBalance: { value: 750, date: today, change: 0 },
      fedBalanceSheet: { value: 7450, date: today, change: 0 },
      reverseRepo: { value: 450, date: today, change: 0 },
      fedReserves: { value: 3200, date: today, change: 0 },
      highYieldSpread: { value: 3.5, date: today, change: 0 },
      treasury3M: { value: 4.35, date: today, change: 0 },
      yieldSpread10Y3M: { value: 0.80, date: today, change: -0.03 },
      m2: { value: 21000, date: today, change: 0 },
      realM2: { value: 8500, date: today, change: 0 },
      velocityOfM2: { value: 1.1, date: today, change: 0 },
    };

    for (const [key, fallbackObj] of Object.entries(govKeys)) {
      const currentObj = data.government[key];
      if (!currentObj || currentObj.value === undefined || currentObj.value === null || currentObj.value === 0) {
        if (!currentObj || !currentObj.value) {
          data.government[key] = currentObj && currentObj.date ? currentObj : fallbackObj;
        }
      }
    }

    // Trust the server-computed yieldSpread10Y3M value (FRED T10Y3M preferred, Yahoo fallback).
    // Do NOT recompute here — recalculation using client-side treasury values can propagate
    // unit errors from Yahoo Finance and override the authoritative FRED spread.
    const ys103 = data.government?.yieldSpread10Y3M;
    if (ys103?.value !== undefined) {
      // Sanity-check logging only — do not modify the value
      if (Math.abs(ys103.value) > 5) {
        console.warn(`[API] Abnormal yieldSpread10Y3M detected: ${ys103.value}%. Check data pipeline.`);
      }
    }

    if (!data.economics || typeof data.economics !== 'object') {
      data.economics = {};
    }

    const ecoFallbacks: Record<string, { value: number; date: string; change: number }> = {
      coreCpi: { value: 3.2, date: today, change: -0.1 },
      coreRetailSales: { value: 3.5, date: today, change: 0.2 },
      corePce: { value: 2.81, date: today, change: -0.02 },
      ppi: { value: 5.51, date: today, change: -0.28 },
      retailSales: { value: 0.6, date: today, change: 0.2 },
      payrolls: { value: 143, date: today, change: -37 },
      initialClaims: { value: 220000, date: today, change: -5000 },
      pmiManufacturing: { value: 50.8, date: today, change: 0.5 },
      pmiServices: { value: 51.4, date: today, change: -0.6 },
    };

    for (const [key, fallbackObj] of Object.entries(ecoFallbacks)) {
      const currentObj = data.economics[key];
      if (!currentObj || currentObj.value === undefined || currentObj.value === null) {
        data.economics[key] = fallbackObj;
      }
    }

    // Ensure releaseDate fallback on all indicators if missing
    const ensureReleaseDate = (obj: any) => {
      if (obj && typeof obj === 'object') {
        if (!obj.releaseDate && obj.date) {
          obj.releaseDate = obj.date;
        }
      }
    };

    if (data.fedFunds) ensureReleaseDate(data.fedFunds);
    if (data.inflation) ensureReleaseDate(data.inflation);
    if (data.unemployment) ensureReleaseDate(data.unemployment);

    if (data.government) {
      for (const k of Object.keys(data.government)) ensureReleaseDate(data.government[k]);
    }
    if (data.economics) {
      for (const k of Object.keys(data.economics)) ensureReleaseDate(data.economics[k]);
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
    return data as MacroIndicators;
  } catch (err) {
    console.error('[fetchMacroIndicators] API call failed:', err);
    // Return a fully populated fallback with all government fields so UI renders correctly
    const today = new Date().toISOString().split('T')[0];
    const fallback: MacroIndicators = {
      fedFunds: { value: 5.33, date: today, change: 0 },
      inflation: { value: 3.2, date: today, change: 0 },
      unemployment: { value: 3.8, date: today, change: 0 },
      sp500: { value: 5500, date: today, change: 0.5 },
      nasdaq: { value: 18500, date: today, change: 0.8 },
      sox: { value: 5100, date: today, change: 0.3 },
      vix: { value: 14.5, date: today, change: -0.5 },
      vixeq: { value: 50, date: today, change: 0.5 },
      fearGreed: { value: 65, date: today, change: 2.5, rating: 'Greed' },
      kospiFgi: { value: 58, date: today, change: 1.2, rating: 'Greed' },
      dxy: { value: 104, date: today, change: 0 },
      yieldSpread: { value: 0.47, date: today, change: 0 },
      bitcoin: { value: 75000, date: today, change: 1.2 },
      gold: { value: 2350, date: today, change: 0.8 },
      silver: { value: 29.5, date: today, change: 1.5 },
      copper: { value: 4.5, date: today, change: 0.5 },
      oil: { value: 82, date: today, change: -0.2 },
      naturalGas: { value: 2.4, date: today, change: 1.2 },
      russell2000: { value: 2100, date: today, change: 0.5 },
      djia: { value: 39500, date: today, change: 0.3 },
      kospi: { value: 2730, date: today, change: 0.5 },
      kosdaq: { value: 850, date: today, change: 0.2 },
      usdkrw: { value: 1350, date: today, change: 0 },
      vkospi: { value: 14.5, date: today, change: 0 },
      ironOre: { value: 115, date: today, change: 0.5 },
      lithium: { value: 14, date: today, change: -1.0 },
      nickel: { value: 19500, date: today, change: 0.5 },
      cobalt: { value: 28300, date: today, change: 0 },
      wheat: { value: 685, date: today, change: -0.5 },
      corn: { value: 460, date: today, change: 0.2 },
      soybeans: { value: 1215, date: today, change: 0.8 },
      bdi: { value: 1845, date: today, change: 0 },
      m2MoneySupply: { value: 21000, date: today, change: 0.1 },
      government: {
        treasury10Y: { value: 4.67, date: today, change: 0.05 },
        treasury5Y: { value: 4.35, date: today, change: 0.03 },
        treasury2Y: { value: 4.20, date: today, change: 0.02 },
        treasury3M: { value: 4.05, date: today, change: 0 },
        yieldSpread10Y3M: { value: 0.62, date: today, change: 0.05 },
        highYieldSpread: { value: 3.5, date: today, change: 0 },
        tgaBalance: { value: 750, date: today, change: 0 },
        fedBalanceSheet: { value: 7450, date: today, change: 0 },
        reverseRepo: { value: 450, date: today, change: 0 },
        fedReserves: { value: 3200, date: today, change: 0 },
        m2: { value: 21000, date: today, change: 0.1 },
        realM2: { value: 8500, date: today, change: -0.5 },
        velocityOfM2: { value: 1.1, date: today, change: 0 },
      },
      economics: {
        coreCpi: { value: 3.2, date: today, change: -0.1 },
        coreRetailSales: { value: 3.5, date: today, change: 0.2 },
        corePce: { value: 2.8, date: today, change: -0.1 },
        ppi: { value: 1.5, date: today, change: 0 },
        retailSales: { value: 0.6, date: today, change: 0.2 },
        payrolls: { value: 175, date: today, change: 10 },
        initialClaims: { value: 220, date: today, change: -5 },
        pmiManufacturing: { value: 50.8, date: today, change: 0.5 },
        pmiServices: { value: 51.4, date: today, change: -0.6 },
      },
      futures: {
        sp500: { value: 5500, date: today, change: 0.1 },
        nasdaq: { value: 18500, date: today, change: 0.2 },
        dow: { value: 39500, date: today, change: 0.1 },
        russell: { value: 2100, date: today, change: 0.3 },
        treasury10y: { value: 110, date: today, change: 0 },
        vix: { value: 14.5, date: today, change: -0.3 },
      },
      sectors: {},
      calendars: { economic: [], earnings: [] },
      isMock: true,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: fallback, timestamp: Date.now() }));
    return fallback;
  }
}

/**
 * FOMC 및 한국은행 중앙은행 공식 기준금리 결정(목표 범위)을 조회합니다.
 */
export async function fetchPolicyRates(): Promise<PolicyRatesData | null> {
  try {
    const res = await fetchWithTimeout('/api/macro/policy-rates', { timeout: 8000 });
    if (!res.ok) throw new Error(`Policy rates fetch failed: ${res.status}`);
    const data = await res.json() as any;
    return data?.rates || null;
  } catch (err) {
    console.error('[fetchPolicyRates] Error:', err);
    return null;
  }
}

export async function fetchMacroState(includeAi: boolean = false, market: string = 'US'): Promise<any> {
  const user = auth.currentUser;
  const res = await fetchWithTimeout(`/api/macro?includeAi=${includeAi}&market=${market}`, {
    headers: user ? { 'Authorization': `Bearer ${await getSafeToken(user)}` } : {},
    timeout: 10000
  });
  return await safeJson(res, null);
}

export async function saveMacroState(macro: any, market: string = 'US'): Promise<void> {
  const sanitized = sanitizeMacroState(macro);
  await fetchWithTimeout(`/api/macro?market=${market}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getAuthHeaders(auth.currentUser))
    },
    body: JSON.stringify(sanitized)
  });
}

// --- Bulk signals fetch: retry with exponential backoff ---
// The /api/signals endpoint reads D1 and may self-heal by fetching fresh prices,
// which can be slow for large universes. Give it a realistic budget and retry.
const SIGNALS_MAX_RETRIES = 2; // 3 total attempts
const SIGNALS_TIMEOUT_MS = 25000;

export async function fetchBulkSignals(symbols: string[] = [], forceRefresh: boolean = false): Promise<MarketSignal[]> {
  const symbolQuery = symbols.length > 0 ? `?symbols=${symbols.join(',')}` : '';
  const refreshQuery = forceRefresh ? `${symbolQuery ? '&' : '?'}forceRefresh=true&_t=${Date.now()}` : '';
  const url = `/api/signals${symbolQuery}${refreshQuery}`;

  try {
    const headers = await getAuthHeaders(auth.currentUser);
    const res = await fetchWithRetry(url, { headers, timeout: SIGNALS_TIMEOUT_MS }, SIGNALS_MAX_RETRIES);
    return await safeJson(res, []);
  } catch (e) {
    console.error("Bulk signals fetch error:", e);
    return [];
  }
}

// --- Bulk price fetch: chunking + retry with exponential backoff ---
// The backend fans out to external providers (Naver/Yahoo) per symbol, so large
// symbol lists can exceed a single request budget. Splitting into small chunks and
// retrying transient failures keeps the live-price polling resilient.
const PRICE_CHUNK_SIZE = 10;
const PRICE_MAX_RETRIES = 2; // 3 total attempts
const PRICE_TIMEOUT_MS = 20000;

async function fetchPriceChunk(
  symbols: string[],
  forceRefresh: boolean,
  headers: Record<string, string>
): Promise<any[]> {
  const symbolQuery = `?symbols=${symbols.join(',')}`;
  const refreshQuery = forceRefresh ? `&forceRefresh=true&_t=${Date.now()}` : '';
  const url = `/api/stocks/prices${symbolQuery}${refreshQuery}`;

  try {
    const res = await fetchWithRetry(url, { headers, timeout: PRICE_TIMEOUT_MS }, PRICE_MAX_RETRIES);
    const data = await safeJson(res, { prices: [] });
    return data.prices || [];
  } catch (lastError) {
    // Resolve with an empty array so a single failed chunk does not discard the
    // partial results gathered from the other chunks.
    console.warn(
      `[fetchBulkPrices] Chunk failed after ${PRICE_MAX_RETRIES + 1} attempts (${symbols.join(',')}):`,
      lastError
    );
    return [];
  }
}

export async function fetchBulkPrices(symbols: string[], forceRefresh: boolean = false): Promise<any[]> {
  if (!symbols || symbols.length === 0) return [];

  // Dedupe (case-insensitive) to avoid redundant backend work
  const seen = new Set<string>();
  const uniqueSymbols: string[] = [];
  for (const s of symbols) {
    if (!s) continue;
    const key = s.toUpperCase();
    if (!seen.has(key)) {
      seen.add(key);
      uniqueSymbols.push(s);
    }
  }
  if (uniqueSymbols.length === 0) return [];

  try {
    // Resolve auth headers once and reuse for every chunk (avoids repeated token fetches)
    const headers = await getAuthHeaders(auth.currentUser);

    // Chunk to keep each request small -> faster responses + avoids URL-length limits
    const chunks: string[][] = [];
    for (let i = 0; i < uniqueSymbols.length; i += PRICE_CHUNK_SIZE) {
      chunks.push(uniqueSymbols.slice(i, i + PRICE_CHUNK_SIZE));
    }

    const chunkResults = await Promise.all(
      chunks.map(chunk => fetchPriceChunk(chunk, forceRefresh, headers))
    );

    return chunkResults.flat();
  } catch (e) {
    console.error("Bulk prices fetch error:", e);
    return [];
  }
}

export async function fetchStockAlternativeData(symbol: string, analysisType: string = 'quantitative', language: string = 'ko', forceRefresh: boolean = false, name?: string): Promise<any> {
  const res = await fetchWithTimeout(`/api/stock/alternative-data?t=${Date.now()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getAuthHeaders(auth.currentUser))
    },
    body: JSON.stringify({ symbol, name, analysisType, language, forceRefresh })
  });
  return await safeJson(res, { error: "Failed to fetch alternative data" });
}

export async function fetchHoldingsFromDB(market: string = 'US', accountId?: string, paperMode?: string): Promise<Holding[]> {
  const user = auth.currentUser;

  try {
    // Do NOT pass accountId=all — backend returns aggregated view when no accountId is given
    const effectiveAccountId = accountId && accountId !== 'all' ? accountId : undefined;
    const paperParam = paperMode ? `&paperMode=${encodeURIComponent(paperMode)}` : '';
    const url = `/api/holdings?market=${market}${effectiveAccountId ? `&accountId=${effectiveAccountId}` : ''}${paperParam}`;
    const res = await fetchWithRetry(url, {
      headers: user ? { 'Authorization': `Bearer ${await getSafeToken(user)}` } : {},
      timeout: 20000
    });
    if (!res.ok) {
      // 401 means auth not ready yet — silently return empty array to avoid crashing useMemo
      console.warn(`[fetchHoldingsFromDB] Response ${res.status} for ${url}, returning []`);
      return [];
    }
    const data = await res.json();
    // Guard: backend should return an array; fall back to [] if not and normalize targetWeight > 1
    if (!Array.isArray(data)) return [];
    return data.map((h: any) => ({
      ...h,
      targetWeight: h.targetWeight > 1 ? Number((h.targetWeight / 100).toFixed(4)) : Number((h.targetWeight || 0).toFixed(4))
    }));
  } catch (e) {
    console.error("Holdings fetch failed:", e);
    return [];
  }
}

export async function fetchWatchlistFromDB(market: string = 'US'): Promise<WatchlistItem[]> {
  const user = auth.currentUser;
  try {
    const res = await fetchWithTimeout(`/api/watchlist?market=${market}`, {
      headers: user ? { 'Authorization': `Bearer ${await getSafeToken(user)}` } : {},
      timeout: 20000
    });
    if (res.ok) return await res.json();
    throw new Error(`Status: ${res.status}`);
  } catch (e) {
    console.error("Watchlist fetch failed:", e);
    return [];
  }
}

export async function saveWatchlistItemToDB(item: { symbol: string, name?: string, market?: string, notes?: string }) {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  try {
    const res = await fetchWithTimeout('/api/watchlist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(item)
    });
    if (!res.ok) throw new Error(`Save failed with status ${res.status}`);
  } catch (e) {
    console.error("Watchlist item save failed:", e);
    throw e;
  }
}

export async function deleteWatchlistItemFromDB(symbol: string, market: string = 'US') {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  try {
    const res = await fetchWithTimeout(`/api/watchlist/${encodeURIComponent(symbol)}?market=${market}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!res.ok) throw new Error(`Delete failed with status ${res.status}`);
  } catch (e) {
    console.error("Watchlist item delete failed:", e);
    throw e;
  }
}

export async function saveHoldingToDB(holding: Partial<Holding> & { symbol: string, name?: string, market?: string }) {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;

  try {
    const res = await fetchWithTimeout('/api/holdings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(holding)
    });
    if (!res.ok) {
      let errorMsg = `Save failed with status ${res.status}`;
      try {
        const errorData = await res.json() as any;
        if (errorData?.message) errorMsg = errorData.message;
        else if (errorData?.error) errorMsg = errorData.error;
      } catch (e) {}
      throw new Error(errorMsg);
    }
  } catch (e) {
    console.error("Holding save failed:", e);
    throw e;
  }
}

export async function updateHoldingInDB(symbol: string, updates: Partial<Holding>, market: string = 'US', accountId?: string) {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  const targetAccountId = accountId || `manual_${user?.uid}`;

  try {
    const res = await fetchWithTimeout(`/api/holdings/${symbol}?market=${market}&accountId=${targetAccountId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ ...updates, accountId: targetAccountId })
    });
    if (!res.ok) throw new Error(`Update failed: ${res.status}`);
  } catch (e) {
    console.error("Holding update failed:", e);
    throw e;
  }
}

export async function deleteHoldingFromDB(symbol: string, market: string = 'US', accountId?: string) {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  const targetAccountId = accountId || `manual_${user?.uid}`;

  try {
    // ★ 1차 시도: 전달받은 accountId로 DELETE
    const res = await fetchWithTimeout(`/api/holdings/${symbol}?market=${market}&accountId=${targetAccountId}`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
  } catch (e) {
    // ★ 2차 시도: accountId='manual'로 fallback (DB에 'manual'로 저장된 경우 대비)
    if (targetAccountId !== 'manual') {
      console.warn(`[deleteHoldingFromDB] 1st attempt with "${targetAccountId}" failed, retrying with "manual"`);
      try {
        const res2 = await fetchWithTimeout(`/api/holdings/${symbol}?market=${market}&accountId=manual`, {
          method: 'DELETE',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res2.ok) return;
      } catch (e2) {
        console.error("Holding delete failed (both attempts):", e2);
        throw e2;
      }
    }
    console.error("Holding delete failed:", e);
    throw e;
  }
}

export async function fetchHistory(period?: string, limit?: number, accountId?: string) {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;

  try {
    const params = new URLSearchParams();
    if (period) params.set('period', period);
    if (limit) params.set('limit', String(limit));
    if (accountId && accountId !== 'all') params.set('accountId', accountId);

    const qs = params.toString();
    const url = `/api/history${qs ? `?${qs}` : ''}`;

    const res = await fetchWithTimeout(url, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error('History fetch failed');
    return await res.json();
  } catch (e) {
    console.error("History fetch error:", e);
    return [];
  }
}

export async function saveHistory(
  macro: any,
  advice: string,
  totalValue: number,
  holdings: any[],
  dailyReturnAmt: number = 0,
  dailyReturnPct: number = 0,
  optimization_data: any = null,
  netCashFlow: number = 0,
  accountId: string = 'all'
) {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;

  try {
    const normalizedTotalVal = totalValue > 100000 ? Math.round((totalValue / 1380.0) * 100) / 100 : totalValue;
    const res = await fetchWithTimeout('/api/history', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        macro_state: macro,
        macro_risk_score: macro?.riskScore ?? macro?.riskMetrics?.riskScore ?? 50,
        ai_advice: advice,
        total_value: normalizedTotalVal,
        currency: 'USD',
        holdings,
        daily_return_amt: dailyReturnAmt,
        daily_return_pct: dailyReturnPct,
        optimization_data,
        net_cash_flow: netCashFlow,
        account_id: accountId
      })
    });
    if (!res.ok) throw new Error(`History save failed: ${res.status}`);
  } catch (e) {
    console.error("History save failed:", e);
    throw e;
  }
}

export async function savePortfolioSnapshot(data: any) {
  // Deprecated in favor of expanded saveHistory
  return saveHistory(
    data.macro_state || {},
    data.ai_advice || "",
    data.total_value,
    data.holdings,
    data.daily_return_amt,
    data.daily_return_pct
  );
}

export async function fetchUserProfile(context?: string): Promise<any> {
  const user = auth.currentUser;
  if (!user) return { notificationsEnabled: false };

  const cacheKey = `mmo_${user.uid}_profile_cache`;

  try {
    const token = await getSafeToken(user);
    const profileUrl = context ? `/api/user/profile?context=${encodeURIComponent(context)}` : '/api/user/profile';
    const res = await fetchWithRetry(profileUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      timeout: 8000,
      retries: 1 // 최대 1회만 재시도하여 누적 타임아웃 방지
    });
    if (res.ok) {
      const data = await res.json();
      if (data && typeof data === 'object') {
        StorageManager.safeSet(cacheKey, data);
      }
      return data;
    }

    // 403 등 권한 거부 또는 미등록 계정 응답인 경우: 오래된 로컬 캐시 즉시 파기 및 우회 차단
    if (res.status === 403) {
      console.warn("[Profile] Server returned 403 Forbidden. Invalidating stale local profile cache.");
      StorageManager.safeRemove(cacheKey);
      const isAdmin = checkIfAdmin();
      return { notificationsEnabled: false, isApproved: isAdmin, role: isAdmin ? 'ADMIN' : 'USER' };
    }
  } catch (e) {
    console.warn("Backend profile fetch failed, attempting cached profile fallback", e);
  }

  // Fallback 1: 오직 네트워크 오류 시에만 유효한 로컬 캐시 프로필 복원 (단, 403 거부 시에는 위에서 이미 제거됨)
  const cached = StorageManager.safeGet<any>(cacheKey, null);
  if (cached && typeof cached === 'object' && cached.isApproved !== undefined) {
    console.info("[Profile] Using cached profile fallback to avoid lock-out");
    return cached;
  }

  // Fallback 2: 관리자 이메일 검증을 통해 관리자 계정 잠금 방지
  const isAdmin = checkIfAdmin();

  return { notificationsEnabled: false, isApproved: isAdmin, role: isAdmin ? 'ADMIN' : 'USER' };
}

export function checkIfAdmin(): boolean {
  const user = auth.currentUser;
  if (!user || !user.email) return false;
  const adminEmails = (import.meta.env.VITE_ADMIN_EMAILS || "").toLowerCase().split(",").map(e => e.trim());
  return adminEmails.includes(user.email.toLowerCase());
}

export async function updateUserProfile(updates: any) {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const token = await getSafeToken(user);
  const res = await fetchWithTimeout('/api/user/profile', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(updates),
    timeout: 10000
  });

  if (!res.ok) throw new Error('Failed to update profile');

  // 성공 시 로컬 캐시 프로필도 동기화
  try {
    const cacheKey = `mmo_${user.uid}_profile_cache`;
    const cached = StorageManager.safeGet<any>(cacheKey, {});
    StorageManager.safeSet(cacheKey, { ...cached, ...updates });
  } catch (e) {
    // ignore
  }
}

export async function deleteUserProfile() {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const res = await fetchWithTimeout('/api/user/profile', {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });

  if (!res.ok) throw new Error('Failed to delete profile');
}

export async function fetchUserNotifications(): Promise<any[]> {
  const user = auth.currentUser;
  if (!user) return [];

  try {
    const res = await fetchWithTimeout('/api/user/notifications', {
      headers: {
        'Authorization': `Bearer ${await user.getIdToken()}`
      }
    });
    if (res.ok) return await res.json();
  } catch (e) {
    console.warn("Backend notifications fetch failed", e);
  }
  return [];
}

export async function markNotificationRead(notificationId: string) {
  const user = auth.currentUser;
  if (!user) return;

  await fetchWithTimeout(`/api/user/notifications/${notificationId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ read: true })
  });
}

export async function markAllNotificationsRead() {
  const user = auth.currentUser;
  if (!user) return;

  await fetchWithTimeout('/api/user/notifications', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ all: true, read: true })
  });
}

export interface PortfolioHwmStatus {
  userId: string;
  hwm: number;
  allTimeMax: number;
  baselineMax: number;
  currentPortfolioValue: number;
  drawdownPct: number;
  mddThreshold: number;
  resetAt: string | null;
  customBaseline: number | null;
}

export async function fetchPortfolioHwm(): Promise<PortfolioHwmStatus | null> {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    const token = await getSafeToken(user);
    if (!token) return null;
    const res = await fetchWithRetry('/api/user/portfolio/hwm', {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      timeout: 12000,
      retries: 1
    });
    if (res.ok) return await res.json();
  } catch (e) {
    console.warn('Failed to fetch portfolio HWM status', e);
  }
  return null;
}

export async function resetPortfolioHwm(value?: number, targetUserId?: string): Promise<{ success: boolean; newHwm?: number; message?: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('Authentication required');

  const token = await user.getIdToken();
  const res = await fetchWithTimeout('/api/user/portfolio/hwm-reset', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      value,
      targetUserId
    })
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(errorData.error || 'Failed to reset HWM');
  }

  return await res.json();
}

export async function snoozePortfolioMddAlert(): Promise<{ success: boolean; message?: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('Authentication required');

  const token = await user.getIdToken();
  const res = await fetchWithTimeout('/api/user/portfolio/mdd-snooze', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(errorData.error || 'Failed to snooze MDD alert');
  }

  return await res.json();
}

export async function saveAnalysisNote(symbol: string, note: string, agentName: string = 'Gemini-2.5-Pro', marketData?: any) {
  if (!checkIfAdmin()) return;

  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  try {
    const res = await fetchWithTimeout('/api/admin/analysis-note', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ symbol, note, agent: agentName, marketData })
    });
    if (!res.ok) throw new Error('Failed to save analysis note');
  } catch (e) {
    console.error("Analysis note save error:", e);
  }
}

export async function fetchCollectorHealth(): Promise<any> {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  try {
    const res = await fetchWithTimeout(`/api/admin/collector-health?t=${Date.now()}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (res.ok) return await res.json();
    return null;
  } catch (e) {
    console.error("Failed to fetch collector health", e);
    return null;
  }
}

export async function fetchSystemLogs(limitCount: number = 20): Promise<any[]> {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  try {
    const res = await fetchWithTimeout(`/api/admin/system-logs?limit=${limitCount}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (res.ok) return await res.json();
    return [];
  } catch (e) {
    console.error("Failed to fetch system logs", e);
    return [];
  }
}

export async function fetchUserAIConfig(): Promise<any> {
  const user = auth.currentUser;
  if (!user) return {};

  try {
    const token = await getSafeToken(user);
    if (!token) return {};
    const res = await fetchWithRetry('/api/user/ai-config', {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      timeout: 12000,
      retries: 1
    });
    if (res.ok) return await res.json();
  } catch (e) {
    console.warn("User AI config fetch failed", e);
  }
  return {};
}

export async function updateUserAIConfig(config: any) {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const res = await fetchWithTimeout('/api/user/ai-config', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify(config)
  });

  if (!res.ok) throw new Error('Failed to update AI config');
}

export async function deleteUserApiKey(provider: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const res = await fetchWithTimeout(`/api/user/api-keys?provider=${encodeURIComponent(provider)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    throw new Error(errorData.error || 'Failed to delete API key');
  }
}

export async function fetchUserApiKeys(): Promise<any[]> {
  const user = auth.currentUser;
  if (!user) return [];

  try {
    const token = await getSafeToken(user);
    if (!token) return [];
    const res = await fetchWithRetry('/api/user/api-keys', {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      timeout: 12000,
      retries: 1
    });
    if (res.ok) return await res.json();
  } catch (e) {
    console.warn("User API keys fetch failed", e);
  }
  return [];
}

export async function updateUserApiKey(provider: string, apiKey: string) {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const res = await fetchWithTimeout('/api/user/api-keys', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ provider, api_key: apiKey })
  });

  if (!res.ok) throw new Error('Failed to update API key');
}

export async function fetchStockHistory(symbol: string, interval: string = '1d', range: string = '1mo'): Promise<any> {
  const res = await fetchWithTimeout('/api/stock/history', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ symbol, interval, range })
  });
  return await safeJson(res, null);
}

export async function fetchStockEvaluation(symbol: string, language: string = 'ko'): Promise<any> {
  const res = await fetchWithTimeout('/api/stock/evaluation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ symbol, language })
  });
  return await safeJson(res, null);
}

export async function scrapePortfolioScreenshot(image: string): Promise<any> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const res = await fetchWithTimeout('/api/portfolio/scrape-screenshot', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ image })
  });
  return await safeJson(res, null);
}

/** user_api_keys(user_api_keys 테이블) 기준으로 해당 증권사 모의 키 등록 여부를 판정합니다. */
export async function isBrokerKeyConfigured(provider: 'kis' | 'ls' | 'kiwoom' | 'toss'): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  try {
    const keys = await fetchUserApiKeys();
    return (keys || []).some((k: any) => k?.provider === provider && Boolean(k?.api_key));
  } catch (e) {
    console.warn(`[isBrokerKeyConfigured] ${provider} check failed`, e);
    return false;
  }
}

// KIWOOM REST OpenAPI Client API Functions
export async function checkKiwoomConfig(): Promise<{ configured: boolean }> {
  return { configured: await isBrokerKeyConfigured('kiwoom') };
}

export async function syncKiwoomHoldings(): Promise<{ success: boolean; count: number; cashSynced: number }> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout('/api/kiwoom/sync', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`,
    },
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    throw new Error(errorData.error || 'Failed to sync Kiwoom holdings');
  }
  return await res.json();
}

// KIS (Korea Investment & Securities) Client API Functions
// [정책/단일 저장소] 증권사 모의투자 자격증명은 서버의 user_api_keys(AES-GCM 암호화)에서만 사용합니다.
//   - 클라이언트는 더 이상 localStorage 평문 키를 헤더로 전송하지 않습니다.
function getKisHeaders(): Record<string, string> {
  return {};
}

export async function checkKisConfig(): Promise<{ configured: boolean }> {
  return { configured: await isBrokerKeyConfigured('kis') };
}

export async function syncKisHoldings(options?: { isPaper?: boolean; mode?: 'live' | 'paper' }): Promise<{ success: boolean; count: number; cashSynced: number; usdCashSynced?: number }> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const params = new URLSearchParams();
  if (options?.isPaper !== undefined) params.set('isPaper', String(options.isPaper));
  if (options?.mode) params.set('mode', options.mode);
  const qStr = params.toString() ? `?${params.toString()}` : '';

  const res = await fetchWithTimeout(`/api/kis/sync${qStr}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`,
      'Content-Type': 'application/json',
      ...getKisHeaders()
    },
    body: JSON.stringify({
      isPaper: options?.isPaper,
      mode: options?.mode
    }),
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    const detailMsg = errorData.details && errorData.details !== errorData.error ? ` (${errorData.details})` : '';
    throw new Error((errorData.error || 'Failed to sync KIS holdings') + detailMsg);
  }
  return await res.json();
}

export async function executeKisOrdersBatch(orders: any[]): Promise<{ success: boolean; results: any[] }> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout('/api/kis/orders/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`,
      ...getKisHeaders()
    },
    body: JSON.stringify({ orders }),
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    const detailMsg = errorData.details && errorData.details !== errorData.error ? ` (${errorData.details})` : '';
    throw new Error((errorData.error || 'Failed to execute KIS batch orders') + detailMsg);
  }
  return await res.json();
}

export async function fetchKisAccounts(appKey: string, appSecret: string, accountNum: string): Promise<any[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout('/api/kis/accounts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ appKey, appSecret, accountNum }),
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    const detailMsg = errorData.details && errorData.details !== errorData.error ? ` (${errorData.details})` : '';
    throw new Error((errorData.error || 'Failed to fetch KIS accounts') + detailMsg);
  }
  const data = await res.json() as any;
  return data.accounts || [];
}

// LS Securities Client API Functions
// [정책/단일 저장소] 증권사 모의투자 자격증명은 서버의 user_api_keys(AES-GCM 암호화)에서만 사용합니다.
//   - 클라이언트는 더 이상 localStorage 평문 키를 헤더로 전송하지 않습니다.
function getLsHeaders(): Record<string, string> {
  return {};
}

/** 모든 증권사(KIS, LS)의 자격증명은 서버 user_api_keys 에서만 조회하므로 전달할 헤더가 없습니다. */
export function getAllBrokerHeaders(): Record<string, string> {
  return {};
}

export async function checkLsConfig(): Promise<{ configured: boolean }> {
  return { configured: await isBrokerKeyConfigured('ls') };
}

export async function syncLsHoldings(): Promise<{ success: boolean; count: number; cashSynced: number; usdCashSynced?: number }> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout('/api/ls/sync', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`,
      ...getLsHeaders()
    },
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    throw new Error(errorData.error || 'Failed to sync LS holdings');
  }
  return await res.json();
}

export async function executeLsOrdersBatch(orders: any[]): Promise<{ success: boolean; results: any[] }> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout('/api/ls/orders/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`,
      ...getLsHeaders()
    },
    body: JSON.stringify({ orders }),
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    throw new Error(errorData.error || 'Failed to execute LS batch orders');
  }
  return await res.json();
}

export async function fetchLsAccounts(appKey: string, appSecret: string, accountNum: string): Promise<any[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout('/api/ls/accounts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ appKey, appSecret, accountNum }),
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    throw new Error(errorData.error || 'Failed to fetch LS accounts');
  }
  const data = await res.json() as any;
  return data.accounts || [];
}

// Toss Securities OpenAPI Client API Functions (실거래 전용 — 샌드박스/모의투자 미제공)
// [정책/단일 저장소] 토스증권 자격증명(clientId/clientSecret/accountSeq)은 서버 user_api_keys(AES-GCM)에만 저장합니다.
export async function checkTossConfig(): Promise<{ configured: boolean }> {
  return { configured: await isBrokerKeyConfigured('toss') };
}

export async function syncTossHoldings(): Promise<{ success: boolean; count: number; cashSynced: number; usdCashSynced?: number }> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout('/api/toss/sync', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    const detailMsg = errorData.details && errorData.details !== errorData.error ? ` (${errorData.details})` : '';
    throw new Error((errorData.error || 'Failed to sync Toss holdings') + detailMsg);
  }
  return await res.json();
}

export async function fetchTossAccounts(clientId: string, clientSecret: string): Promise<any[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout('/api/toss/accounts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ clientId, clientSecret }),
    timeout: 25000
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    const detailMsg = errorData.details && errorData.details !== errorData.error ? ` (${errorData.details})` : '';
    throw new Error((errorData.error || 'Failed to fetch Toss accounts') + detailMsg);
  }
  const data = await res.json() as any;
  return data.accounts || [];
}


export async function fetchUserAccounts(paperMode?: string): Promise<any[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const token = await getSafeToken(user);
  if (!token) return [];
  const paperQuery = paperMode ? `?paperMode=${encodeURIComponent(paperMode)}` : '';
  const res = await fetchWithRetry(`/api/accounts${paperQuery}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    timeout: 12000,
    retries: 1
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    throw new Error(errorData.error || 'Failed to fetch user accounts');
  }
  const data = await res.json() as any;
  return data.accounts || [];
}

/**
 * @deprecated Use fetchUserAccounts() instead.
 */
export async function fetchMyDataAccounts(): Promise<any[]> {
  return fetchUserAccounts();
}

/**
 * @deprecated Codef MyData service has been removed.
 */
export async function connectMyDataAccounts(): Promise<any[]> {
  throw new Error('Codef MyData service has been deprecated. Please use official brokerage OpenAPI integration.');
}

/**
 * @deprecated Codef MyData service has been removed.
 */
export async function connectMyDataAccount(
  _organization: string,
  ..._args: any[]
): Promise<any> {
  throw new Error('Codef MyData service has been deprecated. Please use official brokerage OpenAPI integration.');
}

/**
 * @deprecated Codef MyData service has been removed.
 */
export async function syncMyDataAssets(_passwords?: Record<string, string>): Promise<any> {
  throw new Error('Codef MyData service has been deprecated. Please use brokerage sync-all.');
}

export async function deleteConnectedAccount(accountId: string): Promise<any[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const res = await fetchWithTimeout(`/api/accounts?id=${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) as any;
    throw new Error(errorData.error || 'Failed to delete connected account');
  }
  const data = await res.json() as any;
  return data.accounts || [];
}

// ─── Signal & Simulation Log APIs ─────────────────────────────────────────────────

/** 시그널 및 시뮬레이션 실행 로그 목록 조회 */
export async function fetchAutoTradeLogs(params?: {
  limit?: number;
  symbol?: string;
  action?: string;
  status?: string;
  after_time?: string;
  execution_mode?: 'LIVE' | 'SIMULATED' | 'ALL';
  today_only?: boolean;
}): Promise<import('../types').AutoTradeLog[]> {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  let apiLogs: import('../types').AutoTradeLog[] = [];
  try {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.symbol) qs.set('symbol', params.symbol);
    if (params?.action) qs.set('action', params.action);
    if (params?.status) qs.set('status', params.status);
    if (params?.after_time) qs.set('after_time', params.after_time);
    if (params?.execution_mode) qs.set('execution_mode', params.execution_mode);
    if (params?.today_only !== false) qs.set('today_only', '1');
    const res = await fetchWithTimeout(`/api/auto-trades${qs.toString() ? '?' + qs.toString() : ''}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (res.ok) {
      const data = await res.json() as any;
      apiLogs = data.logs || [];
    }
  } catch (e) {
    console.warn('[fetchAutoTradeLogs] API request failed, checking local storage fallback.', e);
  }

  // Merge with localStorage fallback logs (오늘 이전에 발생한 레거시 로컬 로그는 필터링 및 자동 정화)
  try {
    const saved = localStorage.getItem('marketmacro_local_autotrade_logs');
    if (saved) {
      const localLogs = JSON.parse(saved) as import('../types').AutoTradeLog[];
      const todayLogs = localLogs.filter(l => {
        if (!l.executedAt) return false;
        const d = new Date(l.executedAt);
        const now = new Date();
        return d.getFullYear() === now.getFullYear() &&
               d.getMonth() === now.getMonth() &&
               d.getDate() === now.getDate();
      });
      // 정화된 당일 로그만 로컬 스토리지에 유지
      if (todayLogs.length !== localLogs.length) {
        localStorage.setItem('marketmacro_local_autotrade_logs', JSON.stringify(todayLogs));
      }
      const existingIds = new Set(apiLogs.map(l => l.id));
      for (const l of todayLogs) {
        if (!existingIds.has(l.id)) {
          apiLogs.push(l);
          existingIds.add(l.id);
        }
      }
      apiLogs.sort((a, b) => new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime());
    }
  } catch (le) { }

  return apiLogs.slice(0, params?.limit || 200);
}

export interface AutoTradeStatusResponse {
  status: 'ACTIVE' | 'IDLE' | 'ERROR' | 'CIRCUIT_OPEN';
  serverTime: string;
  dailyTradeCount: number;
  dailyTotalAmount: number;
  dailyTotalAmountUS?: number;
  dailyTotalAmountKR?: number;
  activeSymbols: number;
  lastExecutionTime: string | null;
  recentLogs: import('../types').AutoTradeLog[];
  holdingsProfitRadar?: Array<{
    symbol: string;
    name: string;
    market: 'US' | 'KR';
    shares: number;
    avgCost: number;
    currentPrice: number;
    marketValue: number;
    unrealizedPnl: number;
    returnPct: number;
    takeProfitTargetPct: number;
    stopLossTargetPct: number;
    radarStatus: 'TAKE_PROFIT_READY' | 'PARTIAL_PROFIT_ZONE' | 'STOP_LOSS_ALERT' | 'HOLDING';
  }>;
  tradingConfig?: {
    daily_order_limit?: number;
    daily_order_limit_us?: number;
    daily_order_limit_kr?: number;
    budget?: number;
    budget_us?: number;
    budget_kr?: number;
    max_weight?: number;
    stop_loss?: number;
    take_profit?: number;
    paper_take_profit?: number;
    partial_profit_taking?: boolean;
    auto_rotation?: boolean;
    target_markets?: ('KR' | 'US')[];
  } | null;
}

/** 시그널 및 시뮬레이션 상태 및 모니터링 요약 조회 */
export async function fetchAutoTradeStatus(params?: {
  execution_mode?: 'LIVE' | 'SIMULATED' | 'ALL';
}): Promise<AutoTradeStatusResponse> {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  try {
    const qs = params?.execution_mode ? `&execution_mode=${params.execution_mode}` : '';
    const res = await fetchWithTimeout(`/api/auto-trades?mode=status${qs}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (res.ok) {
      return await res.json() as AutoTradeStatusResponse;
    }
  } catch (e) {
    console.warn('[fetchAutoTradeStatus] API request failed, fallback to local status.', e);
  }

  // Fallback to local storage
  let localLogs: import('../types').AutoTradeLog[] = [];
  try {
    const saved = localStorage.getItem('marketmacro_local_autotrade_logs');
    if (saved) {
      localLogs = JSON.parse(saved) as import('../types').AutoTradeLog[];
    }
  } catch (e) { }

  const yesterday = Date.now() - 24 * 60 * 60 * 1000;
  const recent24h = localLogs.filter(l => new Date(l.executedAt).getTime() >= yesterday);
  const totalAmt = recent24h.reduce((acc, l) => acc + (l.totalAmount || 0), 0);
  const totalAmtUS = recent24h.filter(l => l.currency === 'USD' || l.market === 'US').reduce((acc, l) => acc + (l.totalAmount || 0), 0);
  const totalAmtKR = recent24h.filter(l => l.currency === 'KRW' || l.market === 'KR').reduce((acc, l) => acc + (l.totalAmount || 0), 0);
  const symbols = new Set(recent24h.map(l => l.symbol)).size;

  return {
    status: 'ACTIVE',
    serverTime: new Date().toISOString(),
    dailyTradeCount: recent24h.length,
    dailyTotalAmount: totalAmt,
    dailyTotalAmountUS: totalAmtUS,
    dailyTotalAmountKR: totalAmtKR,
    activeSymbols: symbols,
    lastExecutionTime: localLogs.length > 0 ? localLogs[0].executedAt : null,
    recentLogs: localLogs.slice(0, 10),
    tradingConfig: null
  };
}

/** 시그널 및 시뮬레이션 실행 로그 단건 저장 */
export async function saveAutoTradeLog(
  log: Omit<import('../types').AutoTradeLog, 'id'> & { id?: string }
): Promise<void> {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  const fullLog = {
    ...log,
    id: log.id || ('atl_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)),
    executedAt: log.executedAt || new Date().toISOString()
  };

  try {
    const res = await fetchWithTimeout('/api/auto-trades', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(fullLog)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as any;
      throw new Error(err.error || 'Failed to save auto-trade log');
    }
  } catch (e) {
    console.warn('[saveAutoTradeLog] API save failed or server error, saving to local storage fallback:', e);
    try {
      const saved = localStorage.getItem('marketmacro_local_autotrade_logs');
      const list = saved ? JSON.parse(saved) : [];
      list.unshift(fullLog);
      localStorage.setItem('marketmacro_local_autotrade_logs', JSON.stringify(list.slice(0, 200)));
    } catch (le) { }
  }
}

/** 시그널 및 시뮬레이션 로그 배치 저장 */
export async function saveAutoTradeLogs(
  logs: (Omit<import('../types').AutoTradeLog, 'id'> & { id?: string })[]
): Promise<void> {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  const now = new Date().toISOString();
  const fullLogs = logs.map(log => ({
    ...log,
    id: log.id || ('atl_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)),
    executedAt: log.executedAt || now
  }));

  try {
    const res = await fetchWithTimeout('/api/auto-trades', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ logs: fullLogs })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as any;
      throw new Error(err.error || 'Failed to batch save auto-trade logs');
    }
  } catch (e) {
    console.warn('[saveAutoTradeLogs] API save failed, saving to local storage fallback:', e);
    try {
      const saved = localStorage.getItem('marketmacro_local_autotrade_logs');
      const list = saved ? JSON.parse(saved) : [];
      list.unshift(...fullLogs);
      localStorage.setItem('marketmacro_local_autotrade_logs', JSON.stringify(list.slice(0, 200)));
    } catch (le) { }
  }
}

/** 시그널 및 시뮬레이션 로그 단건 삭제 */
export async function deleteAutoTradeLog(id: string): Promise<void> {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  try {
    await fetchWithTimeout(`/api/auto-trades?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
  } catch (e) {
    console.warn('[deleteAutoTradeLog]', e);
  }
  try {
    const saved = localStorage.getItem('marketmacro_local_autotrade_logs');
    if (saved) {
      const list = JSON.parse(saved) as any[];
      const filtered = list.filter(l => l.id !== id);
      localStorage.setItem('marketmacro_local_autotrade_logs', JSON.stringify(filtered));
    }
  } catch (le) { }
}

/** 포트폴리오 X-Ray 및 숨은 리스크 데이터 조회 */
export async function fetchPortfolioXRay(market: string = 'US'): Promise<any> {
  const user = auth.currentUser;
  const token = user ? await getSafeToken(user) : null;
  const res = await fetchWithTimeout(`/api/portfolio/xray?market=${market}`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    timeout: 45000
  });
  if (!res.ok) {
    throw new Error('Failed to fetch Portfolio X-Ray data');
  }
  return res.json();
}

// Device Authentication & Limit Management API
export async function registerCurrentDevice(): Promise<any> {
  const user = auth.currentUser;
  if (!user) return null;
  const headers = await getAuthHeaders(user);
  const payload = getDeviceInfoPayload();

  const res = await fetchWithRetry('/api/auth/register-device', {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    timeout: 8000,
    retries: 1
  });

  const data = await res.json() as any;
  if (!res.ok && res.status === 403 && data?.code === 'DEVICE_LIMIT_EXCEEDED') {
    return data;
  }
  if (!res.ok) {
    throw new Error(data?.error || data?.message || 'Failed to register device');
  }
  return data;
}

export async function fetchUserDevices(): Promise<any> {
  const user = auth.currentUser;
  if (!user) return { devices: [], membershipTier: 'TIER1', maxDevicesAllowed: 1 };
  const headers = await getAuthHeaders(user);
  const res = await fetchWithTimeout('/api/user/devices', {
    headers
  });
  if (!res.ok) throw new Error('Failed to fetch registered devices');
  return res.json();
}

export async function deleteUserDevice(deviceId: string): Promise<any> {
  const user = auth.currentUser;
  if (!user) return null;
  const headers = await getAuthHeaders(user);
  const res = await fetchWithTimeout(`/api/user/devices?deviceId=${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
    headers
  });
  if (!res.ok) throw new Error('Failed to remove device');
  return res.json();
}

export async function swapUserDevice(targetDeviceIdToRemove: string): Promise<any> {
  const user = auth.currentUser;
  if (!user) return null;
  const headers = await getAuthHeaders(user);
  const payload = getDeviceInfoPayload();

  const res = await fetchWithTimeout('/api/user/devices/swap', {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      targetDeviceIdToRemove,
      newDevice: payload
    })
  });
  if (!res.ok) throw new Error('Failed to swap device');
  return res.json();
}

export async function fetchUserTradingConfig(): Promise<any> {
  const user = auth.currentUser;
  if (!user) return null;
  const headers = await getAuthHeaders(user);
  const res = await fetchWithRetry('/api/user/trading-config', {
    headers,
    timeout: 12000,
    retries: 1
  });
  if (!res.ok) throw new Error('Failed to fetch trading configuration');
  return res.json();
}

export async function updateUserTradingConfig(config: {
  enabled: boolean;
  broker?: 'kis' | 'ls' | 'kiwoom' | 'toss' | 'smart_routing';
  budget?: number;
  max_weight?: number;
  stop_loss?: number;
  mdd_breaker?: number;
  daily_order_limit?: number;
  budget_us?: number;
  daily_order_limit_us?: number;
  budget_kr?: number;
  daily_order_limit_kr?: number;
  paper_key?: string;
  paper_secret?: string;
  paper_account?: string;
  is_paper_trading?: boolean;
  order_execution_mode?: 'hitl' | 'auto';
  target_markets?: ('KR' | 'US')[];
  schedule_mode?: 'MARKET_HOURS_ONLY' | 'ALL_DAY';
  use_integrated_margin?: boolean;
  us_execution_session?: 'REGULAR_ONLY' | 'PRE_MARKET' | 'ALLOW_DAY_MARKET';
  max_spread_pct?: number;
  alert_channel_type?: 'DISCORD' | 'SLACK' | 'TELEGRAM';
  webhook_url?: string;
  telegram_chat_id?: string;
  simulation_mode?: 'hitl' | 'auto';
  paper_execution_mode?: 'VIRTUAL_SANDBOX' | 'BROKER_PAPER';
  virtual_seed_krw?: number;
  virtual_seed_usd?: number;
  paper_seed_krw?: number;
  paper_seed_usd?: number;
  paper_schedule_mode?: 'MARKET_HOURS_ONLY' | 'ALL_DAY';
  paper_order_execution_mode?: 'hitl' | 'auto';
  paper_max_weight?: number;
  paper_stop_loss?: number;
  paper_mdd_breaker?: number;
  paper_daily_order_limit_kr?: number;
  paper_daily_order_limit_us?: number;
  paper_broker?: 'kis' | 'ls' | 'kiwoom' | 'toss' | 'smart_routing';
}): Promise<any> {
  const user = auth.currentUser;
  if (!user) return null;
  const headers = await getAuthHeaders(user);
  const res = await fetchWithTimeout('/api/user/trading-config', {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(config)
  });
  if (!res.ok) throw new Error('Failed to update trading configuration');
  return res.json();
}

export async function testUserWebhook(payload: {
  channelType: 'DISCORD' | 'SLACK' | 'TELEGRAM';
  webhookUrl?: string;
  telegramChatId?: string;
}): Promise<{ success: boolean; message?: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const headers = await getAuthHeaders(user);
  const res = await fetchWithTimeout('/api/user/test-webhook', {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  const data = (await res.json()) as any;
  if (!res.ok) throw new Error(data.error || '웹훅 테스트 전송 실패');
  return data;
}

// ─── 주요 증권사(한국투자 · LS · 키움) 통합 API ─────────────────────────────

/** 주요 증권사 계좌 통합 동기화 (스마트 하이브리드 지원) */
export async function syncAllBrokerages(options?: { force?: boolean; isPaper?: boolean; mode?: 'live' | 'paper' }): Promise<any> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const headers = await getAuthHeaders(user);
  const isForce = !!options?.force;
  const params = new URLSearchParams();
  if (isForce) params.set('force', 'true');
  if (options?.isPaper !== undefined) params.set('isPaper', String(options.isPaper));
  if (options?.mode) params.set('mode', options.mode);
  const qStr = params.toString() ? `?${params.toString()}` : '';

  const res = await fetchWithTimeout(`/api/brokerage/sync-all${qStr}`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      force: isForce,
      isPaper: options?.isPaper,
      mode: options?.mode
    })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err.error || 'Failed to sync all brokerages');
  }
  return res.json();
}

/** 대시보드/마이페이지 진입 시 스마트 캐시 검증형 백그라운드 동기화 (Lazy Sync) */
export async function checkAndLazySyncBrokerages(): Promise<any> {
  return syncAllBrokerages({ force: false });
}

/** 2대 증권사 미체결 주문 통합 조회 */
export async function fetchPendingOrders(): Promise<any> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const headers = await getAuthHeaders(user);
  const res = await fetchWithTimeout('/api/orders/pending', {
    headers
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err.error || 'Failed to fetch pending orders');
  }
  return res.json();
}

/** 스마트 브로커 주문 분할 & 배치 실행 */
export async function executeSmartSplitOrders(orders: Array<{
  symbol: string;
  side: 'BUY' | 'SELL';
  orderType?: 'LIMIT' | 'MARKET';
  price?: number;
  quantity: number;
  market?: 'US' | 'KR';
  preferredProvider?: 'kis' | 'ls';
}>): Promise<any> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const headers = await getAuthHeaders(user);
  const res = await fetchWithTimeout('/api/orders/smart-split', {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ orders })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err.error || 'Failed to execute smart split orders');
  }
  return res.json();
}

/** LS증권 옵션 체인 및 Greeks / IV Rank 수집 및 반환 */
export async function fetchLsOptionsChain(symbol: string = '005930'): Promise<any> {
  const user = auth.currentUser;
  const headers = user ? await getAuthHeaders(user) : {};
  const res = await fetchWithTimeout(`/api/ls/options-chain?symbol=${encodeURIComponent(symbol)}`, {
    headers
  });
  if (!res.ok) {
    throw new Error('Failed to fetch LS options chain');
  }
  return res.json();
}

/** LS증권 공매도 잔고 및 수량 수집/조회 */
export async function fetchLsShortSelling(symbol: string = '005930'): Promise<any> {
  const user = auth.currentUser;
  const headers = user ? await getAuthHeaders(user) : {};
  const res = await fetchWithTimeout(`/api/ls/short-selling?symbol=${encodeURIComponent(symbol)}`, {
    headers
  });
  if (!res.ok) {
    throw new Error('Failed to fetch LS short selling data');
  }
  return res.json();
}

/** 실시간 에이전트 및 워커 텔레메트리 파이프라인 상태 조회 */
export async function fetchAgentTelemetry(): Promise<any> {
  const user = auth.currentUser;
  const headers = user ? await getAuthHeaders(user) : {};
  const res = await fetchWithTimeout('/api/agent/telemetry', { headers });
  if (!res.ok) {
    throw new Error('Failed to fetch agent telemetry');
  }
  return res.json();
}

/** 글로벌 마켓별(KRX, US, Crypto) 실시간 세션 및 일정 조회 */
export async function fetchMarketScheduleSessions(): Promise<{
  success: boolean;
  timestamp: string;
  sessions: Record<string, any>;
  userConfig?: { target_markets?: string[]; schedule_mode?: string };
}> {
  const user = auth.currentUser;
  const headers = user ? await getAuthHeaders(user) : {};
  const res = await fetchWithTimeout('/api/agent/market-schedule', { headers });
  if (!res.ok) {
    throw new Error('Failed to fetch market schedules');
  }
  return res.json();
}

/** 특정 태스크의 XAI 전략 제안 상세 리포트 조회 */
export async function fetchTaskStrategy(taskId: string): Promise<any> {
  const user = auth.currentUser;
  const headers = user ? await getAuthHeaders(user) : {};
  const res = await fetchWithTimeout(`/api/agent/tasks/${encodeURIComponent(taskId)}/strategy`, { headers });
  if (!res.ok) {
    throw new Error('Failed to fetch task strategy');
  }
  return res.json();
}

export interface AgentStrategyRule {
  id: string;
  name: string;
  metric: string;
  operator: '>' | '<' | '>=' | '<=' | '==';
  value: number;
  priority: number;
  taskType: string;
  isActive: boolean;
  description: string;
  isCurrentlyTriggered?: boolean;
  currentMetricValue?: number | null;
}

/** admin_config 기반 활성화된 에이전트 전략 트리거 규칙 목록 및 실시간 감지 상태 조회 */
export async function fetchAgentStrategies(): Promise<AgentStrategyRule[]> {
  const user = auth.currentUser;
  const headers = user ? await getAuthHeaders(user) : {};
  const res = await fetchWithTimeout('/api/agent/strategies', {
    method: 'GET',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    }
  });
  if (!res.ok) {
    throw new Error('Failed to fetch agent strategies');
  }
  return res.json();
}

/** 전 시스템 실시간 데이터 즉각 취합 및 신규 전략 오케스트레이션 실행 */
export async function triggerInstantAgentOrchestration(
  marketMode?: string, 
  taskType: string = 'REBALANCE', 
  customGoal?: string,
  ruleId?: string,
  description?: string,
  executionMode?: 'PAPER' | 'LIVE'
): Promise<any> {
  const user = auth.currentUser;
  const headers = user ? await getAuthHeaders(user) : {};
  const brokerHeaders = getAllBrokerHeaders();
  const defaultGoals: Record<string, string> = {
    REBALANCE: '목표 비중 괴리율 기반 자율 리밸런싱',
    TACTICAL_BUY: '퀀트 팩터 최상위 유망주 발굴 및 전술적 기회 매수',
    MACRO_ALERT: '시장 변동성(VIX/금리) 위험 진단 및 포트폴리오 방어 제안',
    FX_HEDGE: '환율 저항선 돌파 대응 원/달러 통화 헤징'
  };
  const goal = customGoal || defaultGoals[taskType] || `${taskType} 기반 실시간 자율 전략 생성`;

  const res = await fetchWithTimeout('/api/agent/orchestrate/instant', {
    method: 'POST',
    headers: {
      ...headers,
      ...brokerHeaders,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      task_type: taskType,
      goal,
      rule_id: ruleId,
      description,
      market_mode: marketMode,
      execution_mode: executionMode
    }),
    timeout: 60000
  });
  if (!res.ok) {
    const errData = (await res.json().catch(() => ({}))) as any;
    throw new Error(errData.error || errData.message || 'Failed to trigger instant agent orchestration');
  }
  return res.json();
}

/** 개장 대기(WAITING_FOR_MARKET_OPEN) 상태 태스크의 즉시 주문 실행 요청 */
export async function executeWaitingAgentTask(taskId: string): Promise<any> {
  const user = auth.currentUser;
  const headers = user ? await getAuthHeaders(user) : {};
  const brokerHeaders = getAllBrokerHeaders();
  const res = await fetchWithTimeout(`/api/agent/tasks/${encodeURIComponent(taskId)}/execute-waiting`, {
    method: 'POST',
    headers: {
      ...headers,
      ...brokerHeaders,
      'Content-Type': 'application/json'
    },
    timeout: 30000
  });
  if (!res.ok) {
    const errData = (await res.json().catch(() => ({}))) as any;
    throw new Error(errData.error || errData.message || 'Failed to execute waiting task');
  }
  return res.json();
}

let systemSecurityPromise: Promise<any> | null = null;
let systemSecurityCache: { data: any; timestamp: number } | null = null;

/**
 * /api/system-security 중복 호출 방지 및 인플라이트 프라미스 공유
 */
export async function fetchSystemSecurityConfig(forceRefresh: boolean = false): Promise<any> {
  const now = Date.now();
  if (!forceRefresh && systemSecurityCache && now - systemSecurityCache.timestamp < 60000) {
    return systemSecurityCache.data;
  }
  if (!systemSecurityPromise || forceRefresh) {
    systemSecurityPromise = (async () => {
      try {
        const response = await fetch('/api/system-security');
        if (response.ok) {
          const data = await response.json();
          systemSecurityCache = { data, timestamp: Date.now() };
          return data;
        }
        return null;
      } catch (err) {
        console.error('[fetchSystemSecurityConfig] Failed:', err);
        return null;
      } finally {
        systemSecurityPromise = null;
      }
    })();
  }
  return systemSecurityPromise;
}

/**
 * 퀀트 거래량 분석 기반 추천 종목 리스트 조회
 */
export async function fetchQuantRecommendations(params?: {
  market?: string;
  signalType?: string;
  minRvol?: number;
  limit?: number;
}): Promise<QuantRecommendation[]> {
  try {
    const query = new URLSearchParams();
    if (params?.market) query.set('market', params.market);
    if (params?.signalType) query.set('signal_type', params.signalType);
    if (params?.minRvol) query.set('min_rvol', String(params.minRvol));
    if (params?.limit) query.set('limit', String(params.limit));

    const res = await fetchWithTimeout(`/api/quant/recommendations?${query.toString()}`, { timeout: 8000 });
    const data = await safeJson(res, null);
    if (data && data.success && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  } catch (err) {
    console.warn('[fetchQuantRecommendations] Failed:', err);
    return [];
  }
}

/**
 * 실시간 거래량 급증(Volume Surge) 상위 종목 조회
 */
export async function fetchVolumeSurges(market: string = 'ALL'): Promise<any[]> {
  try {
    const res = await fetchWithTimeout(`/api/quant/volume-surges?market=${encodeURIComponent(market)}`, { timeout: 8000 });
    const data = await safeJson(res, null);
    if (data && data.success && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  } catch (err) {
    console.warn('[fetchVolumeSurges] Failed:', err);
    return [];
  }
}

/**
 * 2단계 인증 (2FA / TOTP) 설정 정보 조회
 */
export interface OtpSetupResponse {
  secret: string;
  otpAuthUrl: string;
  issuer: string;
  account: string;
  otpEnabled: boolean;
  otpVerifiedAt?: string | null;
  error?: string;
}

export async function fetchUserOtpSetup(): Promise<OtpSetupResponse> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await getSafeToken(user);
  const res = await fetchWithTimeout('/api/user/otp', {
    headers: { 'Authorization': `Bearer ${token}` },
    timeout: 8000
  });
  if (!res.ok) {
    const errData = await safeJson(res, {});
    throw new Error(errData.error || '2FA 설정 정보를 불러오지 못했습니다.');
  }
  return res.json();
}

/**
 * 2단계 인증 (2FA / TOTP) 활성화 (6자리 코드 검증)
 */
export async function enableUserOtp(secret: string, code: string): Promise<{ success: boolean; message?: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await getSafeToken(user);
  const res = await fetchWithTimeout('/api/user/otp', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ action: 'enable', secret, code }),
    timeout: 8000
  });
  const data = await safeJson(res, {});
  if (!res.ok) {
    throw new Error(data.error || '2FA 활성화에 실패했습니다.');
  }
  return data;
}

/**
 * 2단계 인증 (2FA / TOTP) 비활성화
 */
export async function disableUserOtp(code?: string): Promise<{ success: boolean; message?: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await getSafeToken(user);
  const res = await fetchWithTimeout('/api/user/otp', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ action: 'disable', code }),
    timeout: 8000
  });
  const data = await safeJson(res, {});
  if (!res.ok) {
    throw new Error(data.error || '2FA 비활성화에 실패했습니다.');
  }
  return data;
}

